import { Formik } from 'formik';
import { isEmpty, isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Avatar } from '@hivebrite/design-system/Avatar';
import { Box } from '@hivebrite/design-system/Box';
import type { ModalElement } from '@hivebrite/design-system/Dialog';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import { getFeedNewPost } from 'hb-react/frontoffice/components/UserPostFormModal/requests/getFeedNewPost';
import { getTopicNewPost } from 'hb-react/frontoffice/components/UserPostFormModal/requests/getTopicNewPost';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

import { ConfirmModal } from './components/ConfirmModal';
import { NotifyPeople } from './components/NotifyPeople';
import { QuillInput } from './components/QuillInput';
import { SubmitButton } from './components/SubmitButton';
import { TopicTags } from './components/TopicTags';
import { makeHandleSubmit } from './requests/makeHandleSubmit';
import { FeedType } from './types';
import type { UserPostFormModalProps } from './types';
import { getInitialValues } from './utils/getInitialValues';

const UserPostFormModal = ({
  isOpen,
  onCloseModal,
  onCreate,
  onUpdate,
  topic,
  type,
  userPost: initialData,
  userPostId,
}: UserPostFormModalProps) => {
  const { flagEvUserPicturesToGlobalStorage, flagHivehackNotifyPeople } =
    useFeatureFlag();
  const { currentUser } = useDefaultAppContexts();
  const ref = useRef<ModalElement>(null);

  const modalElement = ref.current?.getModalElement();
  const modalHeaderId = ref.current?.getHeaderElement?.()?.getAttribute('id');
  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint === 'l' || breakpoint === 'xl';

  const [isConfirmActive, setIsConfirmActive] = useState(false);
  const [isEmojiPickerActive, setIsEmojiPickerActive] = useState(false);
  const [userPost, setUserPost] = useState(initialData);

  const isEditingPost = !!userPostId;
  const isLoading = isEmpty(userPost);
  const isTopicPost = [
    FeedType.GROUP_FEED_TYPE,
    FeedType.MY_GROUPS_FEED_TYPE,
  ].includes(type);
  const shouldShowNotifyPeople = !isTopicPost || flagHivehackNotifyPeople;

  const closeButtonText = isEditingPost
    ? s__('Accessibility|Close edit a post window')
    : s__('Accessibility|Close create a post window');

  const header = isEditingPost
    ? __('Edit post')
    : s__('LiveFeed|Write a post · %{topicName}').i__({
        topicName: isTopicPost && topic ? topic.name : __('global feed'),
      });

  const footer = (
    <>
      <NotifyPeople
        isEditingPost={isEditingPost}
        shouldShowNotifyPeople={shouldShowNotifyPeople}
        userPost={userPost}
      />
      <SubmitButton isEditingPost={isEditingPost} />
    </>
  );

  const initialValues = getInitialValues({
    isTopicPost,
    topicId: topic?.id,
    userPost,
  });

  const handleSubmit = (values) => {
    if (userPost?.id && currentUser?.id) {
      makeHandleSubmit({
        currentUserId: currentUser.id,
        isEditingPost,
        isTopicPost,
        onCloseModal,
        onCreate,
        onUpdate,
        topic,
        type,
        userPostId: userPost.id,
        values,
      });
    }
  };

  useEffect(() => {
    const getNewUserPost = async () => {
      const post =
        isTopicPost && topic?.id
          ? await getTopicNewPost({ topicId: topic.id })
          : await getFeedNewPost({ currentUserId: currentUser!.id });

      if (post) {
        setUserPost(post);
      }
    };

    // We have to fetch a new post if the user is creating a new one
    if (!initialData) {
      getNewUserPost();
    }
  }, [initialData, isTopicPost, topic, currentUser]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ initialValues, values }) => (
        <>
          <ModalWithTemplate
            enableEscAction={!isEmojiPickerActive}
            footer={footer}
            header={header}
            isLoading={isLoading}
            isOpen={isOpen}
            isSticky
            // Only use Modal V2 on mobile due to https://hivebrite.atlassian.net/browse/PS-13099
            forceUsageOfV1={isDesktop}
            onCloseModal={() =>
              isEqual(initialValues, values)
                ? onCloseModal()
                : setIsConfirmActive(true)
            }
            pb={0}
            ref={ref}
            screenReadersCloseButtonText={closeButtonText}
          >
            {isLoading || !currentUser ? null : (
              <Box
                alignItems={{ _: 'normal', m: 'flex-start' }}
                display={{ _: 'block', m: 'flex' }}
                width="100%"
              >
                <Box alignItems="center" mb={{ _: 6, m: 0 }}>
                  <AvatarIcon
                    src={
                      flagEvUserPicturesToGlobalStorage
                        ? currentUser.new_photo?.thumbnail_url || ''
                        : currentUser.photo_thumb_url
                    }
                  />
                  <Box display={{ _: 'block', m: 'none' }} font="h3-semibold">
                    {currentUser.name}
                  </Box>
                </Box>

                <Box display="block" width="100%" minWidth={0}>
                  <QuillInput
                    ariaLabelledBy={modalHeaderId}
                    containerElement={modalElement}
                    initialEmbed={initialValues.post.embed}
                    onToggleEmojiPicker={setIsEmojiPickerActive}
                  />
                  {topic ? (
                    <TopicTags isTopicPost={isTopicPost} topic={topic} />
                  ) : null}
                </Box>

                <ErrorFocus />
              </Box>
            )}
          </ModalWithTemplate>

          <ConfirmModal
            isEditingPost={isEditingPost}
            isOpen={isConfirmActive}
            isTopicPost={isTopicPost}
            onCloseModal={onCloseModal}
            setDisplayConfirmModal={setIsConfirmActive}
            topicId={topic?.id}
            userPostId={userPost?.id}
          />
        </>
      )}
    </Formik>
  );
};

export default UserPostFormModal;

const AvatarIcon = styled(Avatar).attrs(() => {
  const size = { _: 48, m: 60 };

  return {
    minHeight: size,
    minWidth: size,
    mr: 3,
    size,
  };
})``;
